import React, { useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import SearchIcon from '@mui/icons-material/Search'
import ArticleIcon from '@mui/icons-material/Article'
import CategoryIcon from '@mui/icons-material/Category'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import GavelIcon from '@mui/icons-material/Gavel'
import BusinessIcon from '@mui/icons-material/Business'
import AssignmentIcon from '@mui/icons-material/Assignment'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import authService from '../../services/authService'

const drawerWidth = 312
const publishRoles = ['ADMIN', 'OPERADOR']

const sectionMeta = {
  general: { title: 'Explorar', subtitle: 'Consulta y acceso general' },
  gestion: { title: 'Operación', subtitle: 'Carga y mantenimiento' },
  configuracion: { title: 'Administración', subtitle: 'Catálogos y relaciones' },
}

const allMenuItems = [
  { label: 'Dashboard', description: 'Portada operativa del sistema', icon: <DashboardRoundedIcon />, path: '/dashboard', section: 'general', roles: ['PUBLIC', 'ASISTENTE', 'ADMIN', 'OPERADOR'] },
  { label: 'Consultar Normas', description: 'Búsqueda, filtros y acceso a PDFs', icon: <SearchIcon />, path: '/normas', section: 'general', roles: ['PUBLIC', 'ASISTENTE', 'ADMIN', 'OPERADOR'] },
  { label: 'Nueva Norma Legal', description: 'Alta y edición de publicaciones', icon: <ArticleIcon />, path: '/nueva', section: 'gestion', roles: publishRoles },
  { label: 'Tipo de Norma Legal', description: 'Catálogo de tipos disponibles', icon: <CategoryIcon />, path: '/tipo-norma-legal', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Voz de Norma Legal', description: 'Administración de voces', icon: <RecordVoiceOverIcon />, path: '/voz-norma-legal', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Unidad Organizativa', description: 'Gestión de unidades', icon: <AccountTreeIcon />, path: '/uo', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Afectación Norma Legal', description: 'Configuración de afectaciones', icon: <GavelIcon />, path: '/afectacion-norma-legal', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Oficina', description: 'ABM de oficinas', icon: <BusinessIcon />, path: '/oficina', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Oficina × Tipo NL', description: 'Relaciones entre oficinas y tipos', icon: <AssignmentIcon />, path: '/oficina-tipo-norma-legal', section: 'configuracion', roles: ['ASISTENTE'] },
  { label: 'Oficina Notif.', description: 'Notificaciones por oficina', icon: <NotificationsActiveIcon />, path: '/oficina-notif', section: 'configuracion', roles: ['ASISTENTE'] },
]

export default function HomeLayout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userRol, setUserRol] = useState('PUBLIC')
  const [userName, setUserName] = useState('')
  const [userDisplay, setUserDisplay] = useState('')
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [authChecking, setAuthChecking] = useState(true)
  const [menuQuery, setMenuQuery] = useState('')
  const runtimeHost = typeof window !== 'undefined' ? window.location.hostname : ''
  const routerMode = (import.meta.env.VITE_ROUTER_MODE || '').toLowerCase()
  const useHashRouter = routerMode === 'hash' || runtimeHost.endsWith('.amplifyapp.com')

  const replaceAppUrl = (targetPath) => {
    if (!targetPath || typeof window === 'undefined') return
    if (useHashRouter) {
      const normalizedPath = targetPath.startsWith('/') ? targetPath : `/${targetPath}`
      window.history.replaceState({}, document.title, `${window.location.pathname}#${normalizedPath}`)
      return
    }
    window.history.replaceState({}, document.title, targetPath)
  }

  const applySessionState = (session) => {
    setUserRol(session.userRol || 'PUBLIC')
    setUserName(session.userName || '')
    setIsLoggedIn(Boolean(session.isLoggedIn))
    setUserDisplay(session.userDisplay || '')
  }

  useEffect(() => {
    let cancelled = false

    const initAuth = async () => {
      setAuthChecking(true)

      const result = await authService.initAuth({
        pathname: location.pathname,
        search: location.search,
      })

      if (cancelled || result.action === 'redirect') {
        return
      }

      if (result.action === 'navigate') {
        replaceAppUrl(result.returnPath)
        navigate(result.to)
        return
      }

      replaceAppUrl(result.returnPath)
      applySessionState(result)

      if (!result.isLoggedIn) {
        for (let i = 0; i < 8; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 120))
          const refreshedSession = authService.getSessionState()
          if (refreshedSession.isLoggedIn) {
            applySessionState(refreshedSession)
            break
          }
        }
      }

      setAuthChecking(false)
    }

    initAuth()

    return () => {
      cancelled = true
    }
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    const syncSession = () => {
      if (authChecking) return
      const session = authService.getSessionState()
      applySessionState(session)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncSession()
      }
    }

    window.addEventListener('focus', syncSession)
    window.addEventListener('storage', syncSession)
    window.addEventListener('pageshow', syncSession)
    document.addEventListener('visibilitychange', onVisibilityChange)

    const startupSyncTimer = setTimeout(syncSession, 120)

    return () => {
      window.removeEventListener('focus', syncSession)
      window.removeEventListener('storage', syncSession)
      window.removeEventListener('pageshow', syncSession)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearTimeout(startupSyncTimer)
    }
  }, [authChecking])

  const visibleMenu = useMemo(
    () => allMenuItems.filter((item) => item.roles.includes(userRol)),
    [userRol]
  )
  const canPublishNormaLegal = publishRoles.includes(userRol)

  const filteredSections = useMemo(() => {
    const query = menuQuery.trim().toLowerCase()

    return Object.entries(sectionMeta)
      .map(([key, meta]) => ({
        key,
        ...meta,
        items: visibleMenu.filter((item) => item.section === key && (!query || `${item.label} ${item.description}`.toLowerCase().includes(query))),
      }))
      .filter((section) => section.items.length > 0)
  }, [menuQuery, visibleMenu])

  const currentItem = visibleMenu.find((item) => location.pathname.startsWith(item.path))

  const handleLogin = () => {
    const returnPath = location.pathname === '/' ? '/normas' : location.pathname
    authService.startInteractiveLogin(returnPath)
  }

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)

  const handleRequestLogout = () => {
    setLogoutConfirmOpen(true)
  }

  const handleConfirmLogout = () => {
    setLogoutConfirmOpen(false)
    authService.logout()
  }

  const handleCancelLogout = () => {
    setLogoutConfirmOpen(false)
  }

  const handleAbandonPage = () => {
    setLogoutConfirmOpen(false)
    setTimeout(() => {
      window.open('', '_self')
      window.close()
    }, 100)
  }

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 1.5, py: 2, gap: 2 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 2,
              bgcolor: '#f5f5f5',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src="/assets/pjlogo1.png"
              alt="Poder Judicial"
              sx={{ height: 36, objectFit: 'contain' }}
              onError={(event) => { event.target.style.display = 'none' }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#666666', display: 'block', letterSpacing: '0.14em' }}>
              PODER JUDICIAL DE MENDOZA
            </Typography>
            <Typography variant="h6" sx={{ color: '#000000', fontWeight: 400, lineHeight: 1.1 }}>
              SPA
            </Typography>
            <Typography variant="body2" sx={{ color: '#666666' }}>
              Sistema de Publicaciones de Actos
            </Typography>
          </Box>
        </Stack>
      </Box>

      <TextField
        size="small"
        value={menuQuery}
        onChange={(event) => setMenuQuery(event.target.value)}
        placeholder="Buscar opción..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#666666' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            color: '#000000',
            bgcolor: '#f8f8f8',
            '& fieldset': { borderColor: '#e0e0e0' },
          },
          '& .MuiInputBase-input::placeholder': { color: '#999999', opacity: 1 },
        }}
      />

      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
        {filteredSections.map((section) => (
          <Box key={section.key} sx={{ mb: 2.5 }}>
            <Box sx={{ px: 1.5, mb: 1.25 }}>
              <Typography variant="overline" sx={{ color: '#666666', letterSpacing: '0.14em' }}>
                {section.title}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#999999' }}>
                {section.subtitle}
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {section.items.map((item) => {
                const selected = location.pathname === item.path

                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.75 }}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(item.path)
                        if (isMobile) setMobileOpen(false)
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                        alignItems: 'flex-start',
                        transition: '0.2s ease',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                        },
                        '&.Mui-selected': {
                          bgcolor: '#e3f2fd',
                          border: '1px solid #0062A9',
                          '&:hover': { bgcolor: '#bbdefb' },
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 42,
                          color: selected ? '#0062A9' : '#666666',
                          mt: 0.25,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={item.description}
                        primaryTypographyProps={{ fontSize: '0.92rem', fontWeight: 500, color: '#000000' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem', sx: { color: '#666666', mt: 0.35 } }}
                      />
                      <ChevronRightRoundedIcon sx={{ color: selected ? '#0062A9' : '#999999', mt: 0.6 }} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: '#f8f8f8',
          border: '1px solid #e0e0e0',
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: '#e3f2fd', color: '#0062A9' }}>
            <AutoAwesomeRoundedIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ color: '#333333', fontWeight: 500 }}>
              {authChecking ? 'Verificando sesión...' : isLoggedIn ? 'Sesión activa' : 'Modo público'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666' }}>
              {authChecking
                ? 'Comprobando acceso con Keycloak'
                : isLoggedIn
                  ? userDisplay
                  : 'Consulta disponible sin autenticación'}
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={authChecking ? 'Comprobando' : isLoggedIn ? `Rol ${userRol}` : 'Invitado'}
          sx={{ bgcolor: '#e3f2fd', color: '#0062A9' }}
        />
        {!authChecking && isLoggedIn && (
          <Button
            fullWidth
            color="inherit"
            onClick={handleRequestLogout}
            startIcon={<LogoutIcon />}
            variant="outlined"
            sx={{
              mt: 1.5,
              borderColor: '#2CB9EE',
              color: '#000000',
              '&:hover': {
                borderColor: '#0062A9',
                bgcolor: 'rgba(44, 185, 238, 0.08)',
              },
            }}
          >
            Logout
          </Button>
        )}
        {!authChecking && !isLoggedIn && (
        <Button
          fullWidth
          color="inherit"
          onClick={handleLogin}
          startIcon={<LoginIcon />}
          variant="outlined"
          sx={{
            mt: 1.5,
            borderColor: '#2CB9EE',
            color: '#000000',
            '&:hover': {
              borderColor: '#0062A9',
              bgcolor: 'rgba(44, 185, 238, 0.08)',
            },
          }}
        >
          Ingresar
        </Button>
        )}
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: 'transparent' }}>
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 8, md: 16 },
          left: { xs: 8, md: 16 },
          right: { xs: 8, md: 16 },
          zIndex: theme.zIndex.appBar,
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, md: 2.5 },
        }}
      >
        <AppBar
          position="static"
          sx={{
            flex: 1,
            minHeight: { xs: 74, md: 86 },
            borderRadius: { md: 5 },
            border: '1px solid #e0e0e0',
            bgcolor: 'white',
            boxShadow: 'none',
            backgroundImage: 'none',
          }}
        >
          <Toolbar sx={{ gap: 1.5, minHeight: { xs: 74, md: 86 }, flexWrap: 'wrap' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15, color: '#000000', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  SPA - NORMAS LEGALES
                </Typography>
              </Box>
            </Stack>

            {!isMobile && (
              <Stack direction="row" spacing={1} sx={{ mr: 1.5 }}>
                {canPublishNormaLegal && (
                  <Button
                    color="inherit"
                    variant="outlined"
                    onClick={() => navigate('/nueva')}
                    sx={{
                      borderColor: '#2CB9EE',
                      color: '#000000',
                      '&:hover': {
                        borderColor: '#0062A9',
                        bgcolor: 'rgba(44, 185, 238, 0.08)',
                      },
                    }}
                  >
                    Publicar
                  </Button>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1} alignItems="center">
              {!authChecking && !isLoggedIn && (
                <Button
                  color="inherit"
                  onClick={handleLogin}
                  startIcon={<LoginIcon />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#2CB9EE',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#0062A9',
                      bgcolor: 'rgba(44, 185, 238, 0.08)',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Ingresar
                  </Box>
                </Button>
              )}

              {!authChecking && isLoggedIn && (
                <Button
                  color="inherit"
                  onClick={handleRequestLogout}
                  startIcon={<LogoutIcon />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#2CB9EE',
                    color: '#000000',
                    '&:hover': {
                      borderColor: '#0062A9',
                      bgcolor: 'rgba(44, 185, 238, 0.08)',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Logout
                  </Box>
                </Button>
              )}

              {!authChecking && isLoggedIn && Boolean(userName) && (
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: { xs: 120, sm: 240 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#000000',
                    ml: 0.5,
                  }}
                  title={userName}
                >
                  {userName}
                </Typography>
              )}
            </Stack>
          </Toolbar>
        </AppBar>
      </Box>

      <Dialog
        open={logoutConfirmOpen}
        onClose={handleCancelLogout}
        aria-labelledby="logout-dialog-title"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            background: '#ffffff',
          },
        }}
      >
        <DialogTitle id="logout-dialog-title" sx={{ fontWeight: 700, pb: 0.5, color: '#000000' }}>
          Confirmación de cierre de sesión
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#333333', fontSize: '0.98rem' }}>
            ¿Está seguro que quiere cerrar sesión?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="outlined" sx={{ borderColor: '#2CB9EE', color: '#0062A9' }} onClick={handleAbandonPage}>
            Cancelar
          </Button>
          <Button variant="contained" sx={{ bgcolor: '#2CB9EE', color: '#000000' }} onClick={handleConfirmLogout}>
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: '92px', md: '124px' },
          ml: 0,
          mr: 0,
          minHeight: { xs: 'calc(100vh - 92px)', md: 'calc(100vh - 124px)' },
          position: 'relative',
          background: '#ffffff',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 3 }, pb: 4, px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1, background: '#ffffff' }}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  )
}